import { useState, useMemo, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  IconButton,
  Button,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  Alert,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import {
  ArrowDownTrayIcon,
  ClockIcon,
  TableCellsIcon,
  ExclamationTriangleIcon,
  PlayIcon,
} from '@heroicons/react/24/outline';
import { pdf } from '@react-pdf/renderer';
import BaseGrid from '../../components/grid/base.grid.tsx';
import MaintenanceReportPdf from './MaintenanceReportPdf';
import { usePermissions } from '../../context/PermissionsContext';

const formatTime = (val) => {
  if (!val) return '—';
  const d = new Date(val);
  if (Number.isNaN(d.getTime())) return String(val);
  return d.toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
};

const formatDateTime = (val) => {
  if (!val) return '—';
  const d = new Date(val);
  if (Number.isNaN(d.getTime())) return String(val);
  return `${d.toLocaleDateString('es-CO', { day: '2-digit', month: '2-digit', year: 'numeric' })} ${d.toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}`;
};

const formatDuration = (seconds) => {
  if (seconds === null || seconds === undefined) return '—';
  const sec = Math.max(0, Number(seconds) || 0);
  const mins = Math.floor(sec / 60);
  const remSec = sec % 60;
  if (mins === 0) return `${remSec}s`;
  return `${mins}m ${remSec}s`;
};

const mapData = (raw) =>
  raw.map((r) => {
    const d = new Date(r.date);
    const fecha = `${d.toLocaleDateString('es-CO', { day: '2-digit', month: '2-digit', year: 'numeric' })} ${d.toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit' })}`;
    return {
      _raw: r,
      id: r.id,
      'N° Mantenimiento': r.id,
      fecha,
      'Hora Inicio': formatTime(r.start_time),
      'Hora Final': formatTime(r.end_time),
      cliente: r.projectData?.customerData?.nombre || '—',
      equipo:
        r.projectData?.nombre ||
        `${r.projectData?.elevatorTypeData?.elevatorType || ''} #${r.projectData?.id || ''}`.trim() ||
        '—',
      tipo: r.maintenance_type || 'Preventivo',
      ejecuto: r.technicianData?.name || '—',
      entrego:
        r.deliveryTechnicianData?.name ||
        (r.status === 'Finalizado' ? r.technicianData?.name || '—' : 'Pendiente'),
      recibe: r.customer_name || 'Pendiente',
      continua: r.continuedByTechnicianData?.name || '—',
      estado: r.status || 'Finalizado',
    };
  });

const fetchImageAsBase64 = async (url) => {
  if (!url) return null;
  if (url.startsWith('data:') || url.startsWith('blob:')) return url;
  try {
    const token = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('Token') || '' : '';
    const authUrl = token
      ? url.includes('?')
        ? `${url}&token=${encodeURIComponent(token)}`
        : `${url}?token=${encodeURIComponent(token)}`
      : url;
    const response = await axios.get(authUrl, { responseType: 'blob' });
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.readAsDataURL(response.data);
    });
  } catch (error) {
    console.error('Error fetching image for PDF:', url, error);
    return null;
  }
};

const handleDownloadPDF = async (report) => {
  try {
    const base = axios.defaults.baseURL || '';
    const getFullUrl = (path) => {
      if (!path) return null;
      if (path.startsWith('data:') || path.startsWith('blob:')) return path;
      const cleanPath = path.startsWith('/') ? path.substring(1) : path;
      return `${base.endsWith('/') ? base : `${base}/`}${cleanPath}`;
    };

    const resReport = await axios.get(`/getMaintenanceReport/${report.id}`);
    const fullReport = resReport.data.data;

    const projectId = report.project_id || report.projectData?.id || fullReport.project_id;
    let group = null;
    try {
      const resGroup = await axios.get(`/getProjectQuestionGroups/${projectId}`);
      group = resGroup.data.data;
    } catch (e) {
      console.warn('Could not load question groups for project:', e);
    }

    const technicianSignatureBase64 = await fetchImageAsBase64(getFullUrl(fullReport.technician_signature));
    const deliveryTechnicianSignatureBase64 = await fetchImageAsBase64(
      getFullUrl(fullReport.delivery_technician_signature || fullReport.technician_signature)
    );
    const customerSignatureBase64 = await fetchImageAsBase64(getFullUrl(fullReport.customer_signature));

    const answersMap = {};
    for (const ans of fullReport.answers || []) {
      const photosBase64 = [];
      for (const p of ans.photos || []) {
        const fullUrl = getFullUrl(p);
        const b64 = await fetchImageAsBase64(fullUrl);
        if (b64) photosBase64.push({ preview: b64, originalUrl: fullUrl, path: p });
        else if (fullUrl) photosBase64.push({ preview: fullUrl, originalUrl: fullUrl, path: p });
      }
      answersMap[ans.question_id] = {
        question_id: ans.question_id,
        optionIds: ans.selected_options || [],
        text: ans.answer_text,
        photos: photosBase64,
      };
    }

    const custData = fullReport.projectData?.customerData || report.projectData?.customerData;
    let principalEmail = custData?.contacto_principal?.correo;
    if (!principalEmail && Array.isArray(custData?.contactos)) {
      const p = custData.contactos.find((c) => String(c.contactoPrincipal) === '1' || c.contactoPrincipal === true) || custData.contactos[0];
      principalEmail = p?.correo;
    }
    if (!principalEmail && custData?.correo) {
      principalEmail = custData.correo;
    }
    if (!principalEmail && custData?.email) {
      principalEmail = custData.email;
    }
    if (!principalEmail && custData?.id) {
      try {
        const comp = sessionStorage.getItem('company');
        if (comp) {
          const clRes = await axios.get(`/getClientes/${comp}?tipo=equipo`);
          const clMatch = (clRes.data.data || []).find((c) => c.id === custData.id);
          if (clMatch) {
            principalEmail = clMatch.contacto_principal?.correo;
          }
        }
      } catch (e) {
        console.warn('Could not fetch client details:', e);
      }
    }

    const projObj = fullReport.projectData || report.projectData || {};
    const equipName = projObj.nombre || projObj.elevatorTypeData?.elevatorType || 'Equipo';

    const blob = await pdf(
      <MaintenanceReportPdf
        data={{
          ...fullReport,
          id: fullReport.id || report.id,
          answers: answersMap,
          technicianSignature: technicianSignatureBase64,
          deliveryTechnicianSignature: deliveryTechnicianSignatureBase64,
          customerSignature: customerSignatureBase64,
          activities: fullReport.activities || [],
          start_time: fullReport.start_time,
          end_time: fullReport.end_time,
        }}
        equipo={{
          id: projObj.id,
          nombre: projObj.nombre,
          customerName: custData?.nombre || projObj.customerName,
          elevatorTypeName: projObj.elevatorTypeData?.elevatorType,
          description: projObj.description,
          stopNumber: projObj.stopNumber,
          capacity: projObj.capacity,
          typeDriveSystemName: projObj.driveSystemData?.typeDriveSystem,
          customerData: {
            ...(custData || {}),
            email: principalEmail,
            correo: principalEmail,
          },
          customerEmail: principalEmail,
        }}
        group={group}
        technicianName={fullReport.technicianData?.name || report.technicianData?.name}
        deliveryTechnicianName={
          fullReport.deliveryTechnicianData?.name ||
          report.deliveryTechnicianData?.name ||
          (fullReport.status === 'Finalizado'
            ? fullReport.technicianData?.name || report.technicianData?.name
            : '')
        }
        continuedByTechnicianName={
          fullReport.continuedByTechnicianData?.name || report.continuedByTechnicianData?.name || ''
        }
        customerName={fullReport.customer_name || ''}
        customerEmail={principalEmail}
        maintenanceType={fullReport.maintenance_type || 'Preventivo'}
        recommendations={fullReport.recommendations || []}
        activities={fullReport.activities || []}
        backendUrl={base}
      />
    ).toBlob();

    const reportId = fullReport.id || report.id;
    const rawCliente = custData?.nombre || report.projectData?.customerName || fullReport.customer_name || 'Cliente';
    const rawEquipo = equipName;
    const rawTipo = fullReport.maintenance_type || report.maintenance_type || 'Preventivo';
    const cleanCliente = String(rawCliente).trim().replace(/[/\\?%*:|"<>]/g, '_');
    const cleanEquipo = String(rawEquipo).trim().replace(/[/\\?%*:|"<>]/g, '_');
    const cleanTipo = String(rawTipo).trim().replace(/[/\\?%*:|"<>]/g, '_');

    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${reportId}-${cleanCliente}-${cleanEquipo}-${cleanTipo}.pdf`;
    link.click();
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error generating PDF:', error);
    alert('No se pudo generar el PDF');
  }
};

export default function AdminMantenimiento() {
  const company = sessionStorage.getItem('company');
  const navigate = useNavigate();
  const { isAdmin } = usePermissions();

  const [rows, setRows] = useState([]);
  const [exportingExcel, setExportingExcel] = useState(false);

  // Admin-only timing modal state
  const [timingModalOpen, setTimingModalOpen] = useState(false);
  const [timingLoading, setTimingLoading] = useState(false);
  const [timingReport, setTimingReport] = useState(null);

  const handleDataChange = useCallback((mappedRows) => {
    setRows(mappedRows || []);
  }, []);

  const customFilters = useMemo(() => {
    const uniqueClients = Array.from(
      new Set(rows.map((r) => r.cliente).filter((v) => v && v !== '—'))
    ).sort();
    const uniqueEquipments = Array.from(
      new Set(rows.map((r) => r.equipo).filter((v) => v && v !== '—'))
    ).sort();

    return [
      {
        key: 'cliente',
        label: 'Cliente',
        type: 'select',
        options: uniqueClients.map((c) => ({ value: c, label: c })),
      },
      {
        key: 'equipo',
        label: 'Equipo',
        type: 'select',
        options: uniqueEquipments.map((eq) => ({ value: eq, label: eq })),
      },
      {
        key: 'tipo',
        label: 'Tipo',
        type: 'select',
        options: [
          { value: 'Preventivo', label: 'Preventivo' },
          { value: 'Correctivo', label: 'Correctivo' },
          { value: 'Emergencia', label: 'Emergencia' },
        ],
      },
      {
        key: 'estado',
        label: 'Estado',
        type: 'select',
        options: [
          { value: 'Finalizado', label: 'Finalizado' },
          { value: 'Pendiente de entrega', label: 'Pendiente de entrega' },
          { value: 'Parcial', label: 'Parcial' },
        ],
      },
    ];
  }, [rows]);

  const handleExportExcel = async () => {
    try {
      setExportingExcel(true);
      const response = await axios.get(`/exportMaintenanceReportsExcel/${company}`, {
        responseType: 'blob',
      });
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Historial_Mantenimientos_${new Date().toISOString().slice(0, 10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting Excel:', error);
      alert('No se pudo exportar el archivo Excel');
    } finally {
      setExportingExcel(false);
    }
  };

  const handleOpenTimingModal = async (report) => {
    setTimingModalOpen(true);
    setTimingLoading(true);
    setTimingReport(null);
    try {
      const res = await axios.get(`/getMaintenanceReport/${report.id}`);
      setTimingReport(res.data?.data || report);
    } catch (error) {
      console.error('Error fetching report timing:', error);
      setTimingReport(report);
    } finally {
      setTimingLoading(false);
    }
  };

  const totalQuestionSeconds = useMemo(() => {
    if (!timingReport?.answers) return 0;
    return timingReport.answers.reduce((acc, a) => acc + (Number(a.duration_seconds) || 0), 0);
  }, [timingReport]);

  return (
    <>
      <BaseGrid
        endpoint={`/getAllMaintenanceReports/${company}`}
        title="Historial de Mantenimientos"
        fields={[]}
        hideCreate
        hideEdit
        hideDelete
        excludeKeys={['_raw', 'id']}
        mapData={mapData}
        onDataChange={handleDataChange}
        customFilters={customFilters}
        extraHeaderActions={
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <Button
              variant="outlined"
              color="success"
              onClick={handleExportExcel}
              disabled={exportingExcel}
              startIcon={<TableCellsIcon className="h-5 w-5" />}
              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, whiteSpace: 'nowrap' }}
            >
              {exportingExcel ? 'Exportando...' : 'Exportar Excel'}
            </Button>
            <Button
              variant="contained"
              color="warning"
              onClick={() => navigate('/mantenimiento/partes-danadas')}
              startIcon={<ExclamationTriangleIcon className="h-5 w-5" />}
              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, whiteSpace: 'nowrap' }}
            >
              Informe Partes Dañadas
            </Button>
          </Stack>
        }
        renderExtraActions={(item) => (
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Tooltip title="Descargar Reporte PDF">
              <IconButton
                size="small"
                onClick={() => handleDownloadPDF(item._raw)}
                sx={{
                  color: '#3b82f6',
                  '&:hover': { bgcolor: 'rgba(59, 130, 246, 0.08)' },
                }}
              >
                <ArrowDownTrayIcon className="h-5 w-5" />
              </IconButton>
            </Tooltip>

            {item._raw?.status === 'Parcial' && (
              <Tooltip title="Continuar Mantenimiento Parcial">
                <IconButton
                  size="small"
                  onClick={() =>
                    navigate(
                      `/mantenimiento/formulario/${item._raw.project_id}?reportId=${item.id}&tipo=${encodeURIComponent(
                        item._raw.maintenance_type || 'Preventivo'
                      )}`
                    )
                  }
                  sx={{
                    color: '#d97706',
                    '&:hover': { bgcolor: 'rgba(217, 119, 6, 0.08)' },
                  }}
                >
                  <PlayIcon className="h-5 w-5" />
                </IconButton>
              </Tooltip>
            )}

            {isAdmin && (
              <Tooltip title="Auditoría de Tiempos por Pregunta (Solo Administrador)">
                <IconButton
                  size="small"
                  onClick={() => handleOpenTimingModal(item._raw)}
                  sx={{
                    color: '#7c3aed',
                    '&:hover': { bgcolor: 'rgba(124, 58, 237, 0.08)' },
                  }}
                >
                  <ClockIcon className="h-5 w-5" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        )}
      />

      {/* Dialog: Tiempos por Pregunta (Exclusivo Administrador) */}
      <Dialog
        open={timingModalOpen}
        onClose={() => setTimingModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
          <ClockIcon className="h-6 w-6 text-purple-600" />
          Auditoría de Tiempos del Mantenimiento #{timingReport?.id || ''}
        </DialogTitle>
        <DialogContent dividers>
          <Alert severity="info" sx={{ mb: 2 }}>
            Esta información de inicio y duración por pregunta es visible <strong>únicamente para el Administrador</strong> y no se incluye en el reporte PDF del cliente.
          </Alert>

          {timingLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
              <CircularProgress />
            </Box>
          ) : timingReport ? (
            <Box>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                sx={{
                  mb: 3,
                  p: 2,
                  borderRadius: 2,
                  bgcolor: '#f8fafc',
                  border: '1px solid #e2e8f0',
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Hora de Inicio Mantenimiento
                  </Typography>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {formatDateTime(timingReport.start_time)}
                  </Typography>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Hora Final (Cierre Ejecución)
                  </Typography>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {formatDateTime(timingReport.end_time)}
                  </Typography>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Tiempo Acumulado en Preguntas
                  </Typography>
                  <Typography variant="subtitle1" fontWeight="bold" color="primary.main">
                    {formatDuration(totalQuestionSeconds)}
                  </Typography>
                </Box>
              </Stack>

              {Array.isArray(timingReport.answers) && timingReport.answers.length > 0 ? (
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f1f5f9' }}>
                      <TableCell sx={{ fontWeight: 'bold' }}>#</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Grupo</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Pregunta</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Respuesta</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Timestamp Respuesta</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }} align="right">
                        Tiempo Demorado
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {timingReport.answers.map((ans, idx) => (
                      <TableRow key={ans.id || idx} hover>
                        <TableCell>{idx + 1}</TableCell>
                        <TableCell>{ans.group_name || 'General'}</TableCell>
                        <TableCell>{ans.question_text || `Pregunta #${ans.question_id}`}</TableCell>
                        <TableCell>{ans.answer_text || '—'}</TableCell>
                        <TableCell>{formatDateTime(ans.answered_at)}</TableCell>
                        <TableCell align="right">
                          <Chip
                            size="small"
                            label={formatDuration(ans.duration_seconds)}
                            color={Number(ans.duration_seconds) > 120 ? 'warning' : 'default'}
                            variant="outlined"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                  Este mantenimiento no contiene preguntas de checklist registradas (ej. Emergencia/Correctivo directo).
                </Typography>
              )}
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTimingModalOpen(false)} variant="contained">
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
